import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import Plotly, { ModeBarButton } from "plotly.js";
import React, { CSSProperties, useId, useRef, useState } from "react";
import useTimeState from "hooks/dashboard/useTimeState";
import Plot from "react-plotly.js";
import { I, T } from "services/lang/LanguageService";
import { getChartSizeValue } from "utils/utils";
import { DatasetDataInfoResponse } from "../../../api/model/dataset/DatasetDataInfoResponse";
import { APIResponse } from "../../../services/HttpService";
import ChartWrapper from "./chart-wrapper/ChartWrapper";
import copyChartToClipBoard from "./chartUtils";
import { clipboardIcon, csvDownloadIcon, questionMarkIcon } from "./icons";
import { DatasetPieProps, PlotDataEx } from "./interfaces";
import DatasetsTitlesShower from "./DatasetsTitlesShower";
import ExportCsvDialog from "./ExportCsvDialog";

const layoutConfig: Partial<Plotly.Layout> = {
  legend: { orientation: "h" },
  margin: { l: 0, r: 0, b: 0, t: 65 },
};

const DatasetPieChart = (props: DatasetPieProps) => {
  const uniqueId = useId();
  const [annotations, setAnnotations] = useState<Plotly.Annotations[]>([]);
  const [titlesDialog, setTitlesDialog] = useState<boolean>(false);
  const [exportCsvDialog, setExportCsvDialog] = useState<boolean>(false);
  const [titlesAndOverriddenTitles, setTitlesAndOverriddenTitles] = useState<
    { title: string; overriddenTitle: string }[][]
  >([]);
  const [selectedChartIndex, setSelectedChartIndex] = useState<number | null>(null);
  const setStatus = useComponentStateCallback(props);
  const hasErrorBeenHandled = useRef(false);

  const showTitles = (index: number) => {
    setSelectedChartIndex(index);
    setTitlesDialog(true);
  };

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const handleExportDialog = (index: number) => {
    setSelectedChartIndex(index);
    setExportCsvDialog(true);
  };

  const chartConfig: Partial<Plotly.Config> = {
    displaylogo: false,
    displayModeBar: props.reportMode ? false : "hover",
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: [
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
    ],
  };

  const separateArray = (
    array: Array<DatasetDataInfoResponse>
  ): Array<Array<DatasetDataInfoResponse>> => {
    return props.charts.map(chart => {
      return array.filter(item =>
        chart.identifiers.some(id =>
          typeof id === "string" ? id === item.identifier : id.identifier === item.identifier
        )
      );
    });
  };

  const promiseHandler = (res: APIResponse<any>) => {
    if (checkIfDataExist(res.data)) {
      const tempAnnotations: Plotly.Annotations[] = [];
      const tempTitlesAndOverriddenTitles: { title: string; overriddenTitle: string }[][] = [];

      const arr = separateArray(res.data).map(arr => {
        const values = extractValues(arr, "sum");
        const unit = extractUnit(arr);

        tempAnnotations.push({
          showarrow: false,
          text: `${sumArray(values).toFixed(props.centerTextConfig?.decimalPlaces ?? 2)} ${unit}`,
          font: {
            size: props.centerTextConfig?.fontSize || 18,
          },
        } as unknown as Plotly.Annotations);

        const createValueWithUnitArray = (): Array<string> => {
          const minimumRepresentableValue = props.minimumRepresentableValue ?? 0;

          return values.map(value => {
            const procentValue = ((value / sumArray(values)) * 100).toFixed(1);
            return Number(procentValue) >= minimumRepresentableValue
              ? `${value} ${unit} (${procentValue}%)`
              : "";
          });
        };

        const labels = extractLabels(arr).map(label => I(label));

        tempTitlesAndOverriddenTitles.push(
          labels.map((label, idx) => ({
            title: arr[idx].title,
            overriddenTitle: label !== arr[idx].title ? label : "",
          }))
        );

        return {
          type: "pie",
          hole: 0.4,
          values,
          labels,
          sort: false,
          hoverinfo: "none",
          hovertemplate: `<b>%{label}</b><br>${T("word.value")}: %{value} ${unit}<br>${T("word.percent")}: %{percent}<extra></extra>`,
          text: createValueWithUnitArray(),
          textinfo: "text",
          textfont: { size: props.reportOptions?.fontSize },
          outsidetextfont: { size: 11 },
        } as unknown as PlotDataEx;
      });

      setAnnotations(tempAnnotations);
      setTitlesAndOverriddenTitles(tempTitlesAndOverriddenTitles);
      return arr;
    }

    return [];
  };

  const checkIfDataExist = (array: Array<DatasetDataInfoResponse>): boolean => {
    return array.some(item => item.data.length > 0);
  };

  const extractLabels = (array: Array<DatasetDataInfoResponse>): Array<string> => {
    const originalTitles = array.map(item => item.title);

    const chartTitles = props.charts.map(chart => {
      if (chart.identifiers.every(id => typeof id === "string")) {
        return originalTitles;
      }
      return chart.identifiers.map(identifier =>
        typeof identifier === "string" ? "" : identifier.overriddenTitle || ""
      );
    });

    return originalTitles.map((title, index) => {
      const chartIndex = props.charts.findIndex(chart =>
        chart.identifiers.some(id =>
          typeof id === "string"
            ? id === array[index].identifier
            : id.identifier === array[index].identifier
        )
      );
      const overriddenTitle = chartIndex !== -1 ? chartTitles[chartIndex][index] : "";
      return I(overriddenTitle || title);
    });
  };

  const extractValues = (array: Array<DatasetDataInfoResponse>, operation: "sum" | "average") => {
    return array.map(obj => {
      const sum = obj.data.reduce((acc, pair) => acc + pair.value, 0);
      const fixed = obj.decimalPlaces ?? 2;
      if (operation === "average") {
        const count = obj.data.length;
        const average = count > 0 ? sum / count : 0;
        return +average.toFixed(fixed);
      }
      return +sum.toFixed(fixed);
    });
  };

  const extractUnit = (array: Array<DatasetDataInfoResponse>) => {
    const units = array.map(item => item.unit as string);
    if (units.length > 0) {
      const referenceString = units[0];
      if (units.every(element => element === referenceString)) {
        return referenceString;
      }
    }
    console.error("Units in Pie Chart do not match");
    return "";
  };

  const sumArray = (arr: Array<number>) => {
    return arr.reduce((acc, num) => acc + num, 0);
  };

  const calculateGrid = (): CSSProperties => {
    const charts = props.charts.filter(chart => chart.identifiers.length > 0).length;
    const { chartsPerRow } = props;

    const getRows = (allCharts: number, divideInto: number): number => {
      return Math.ceil(allCharts / divideInto);
    };

    if (!chartsPerRow) {
      return charts <= 3
        ? { gridTemplateColumns: `repeat(${charts}, 1fr)`, gridTemplateRows: `repeat(1, 1fr)` }
        : {
            gridTemplateColumns: `repeat(3, 1fr)`,
            gridTemplateRows: `repeat(${getRows(charts, 3)}, 1fr)`,
          };
    }

    return {
      gridTemplateColumns: `repeat(${chartsPerRow > charts ? charts : chartsPerRow}, 1fr)`,
      gridTemplateRows: `repeat(${getRows(charts, chartsPerRow)}, 1fr)`,
    };
  };

  if (!props.charts || props.charts.length === 0) {
    return (
      <h2 style={{ display: "flex", justifyContent: "center" }}>
        {T("message.pie-chart.no-chart-identifiers")}
      </h2>
    );
  }

  const statusHandler = (status: string) => {
    if (status === "error" && !hasErrorBeenHandled.current) {
      hasErrorBeenHandled.current = true;
      setStatus({ state: status });
    }
  };

  return (
    <ChartWrapper
      id={props.id}
      uniqueKey={uniqueId}
      identifiers={props.charts
        .flatMap(chart => chart.identifiers)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map(identifierObj =>
          typeof identifierObj === "string" ? identifierObj : identifierObj.identifier
        )}
      showDateSelection={props.showDateSelection}
      promiseHandler={promiseHandler}
      updateFrequency={props.updateFrequency}
      loadConf={props.loadConf}
      dateFrom={timeState.from}
      dateTo={timeState.to}
      hideRefresh={props.hideRefresh}
      dynamicHeight={props.dynamicHeight}
      chartConfig={chartConfig}
      referenceTime={props.referenceTime}
      datasetChart="pie"
      width={props.width}
      reportMode={props.reportMode}
      height={props.height}
      getMostRecent={props.getMostRecent}
      hideHeader={props.hideHeader}
      statusHandler={statusHandler}
    >
      {(wrapper: any) =>
        wrapper.data.length > 0 ? (
          <div style={{ display: "grid", gridGap: "10px", ...calculateGrid() }}>
            {wrapper.data.map((data: Plotly.Data, index: number) => {
              const hasOverriddenTitles = titlesAndOverriddenTitles[index]?.some(
                titleObj => titleObj.overriddenTitle !== ""
              );
              const localChartConfig = {
                ...wrapper.chartConfig,
                modeBarButtonsToAdd: [
                  ...wrapper.chartConfig.modeBarButtonsToAdd,
                  hasOverriddenTitles && {
                    title: T("chart-config.show-titles"),
                    name: "Show Titles",
                    icon: questionMarkIcon,
                    click: () => showTitles(index),
                  },
                  {
                    title: `${T("action.download")} CSV`,
                    name: "Download CSV",
                    icon: csvDownloadIcon,
                    click: () => handleExportDialog(index),
                  },
                ].filter(Boolean) as ModeBarButton[],
              };
              return data.labels.length > 0 ? (
                <>
                  {titlesDialog && selectedChartIndex === index && (
                    <DatasetsTitlesShower
                      dialog={titlesDialog}
                      setDialog={setTitlesDialog}
                      titlesAndOverriddenTitles={titlesAndOverriddenTitles[index]}
                    />
                  )}
                  {exportCsvDialog && selectedChartIndex === index && (
                    <ExportCsvDialog
                      dialog={exportCsvDialog}
                      setDialog={setExportCsvDialog}
                      data={data}
                      chartType="PIE"
                    />
                  )}
                  <Plot
                    key={index}
                    data={[data]}
                    className="ea-plotly-pie-chart"
                    config={localChartConfig}
                    layout={{
                      ...layoutConfig,
                      annotations: [{ ...annotations[index] }],
                      height: getChartSizeValue(wrapper.height),
                      title: I(props.charts[index].chartName || ""),
                    }}
                    onError={() => setStatus({ state: "error" })}
                    onInitialized={() => setStatus({ state: "finished" })}
                  />
                </>
              ) : null;
            })}
          </div>
        ) : (
          <h2 style={{ display: "flex", justifyContent: "center" }}>{T("word.no-data")}</h2>
        )
      }
    </ChartWrapper>
  );
};

export default DatasetPieChart;
